import { useEffect, useState, Fragment } from "react";
import { api, formatApiError } from "@/lib/config";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Loader2, Mail, Plus, Trash2, Copy, Check, RefreshCw,
  CheckCircle2, AlertTriangle, Clock,
} from "lucide-react";
import { toast } from "sonner";

function StatusPill({ status }) {
  const map = {
    verified: { c: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30", label: "Verified", icon: CheckCircle2 },
    pending: { c: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: "Pending", icon: Clock },
    not_started: { c: "bg-white/5 text-white/50 border-white/10", label: "Not started", icon: Clock },
    failed: { c: "bg-red-500/15 text-red-300 border-red-500/30", label: "Failed", icon: AlertTriangle },
    temporary_failure: { c: "bg-amber-500/15 text-amber-300 border-amber-500/30", label: "Temporary failure", icon: AlertTriangle },
  };
  const cfg = map[status] || map.not_started;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] tracking-wide border ${cfg.c}`}
          data-testid={`domain-status-${status}`}>
      <Icon size={12} /> {cfg.label}
    </span>
  );
}

function CopyField({ value, dense = false }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    try {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {}
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`group flex items-center gap-2 text-left ${dense ? "px-2 py-1" : "px-3 py-2"} rounded-md bg-white/5 hover:bg-white/10 border border-white/10 w-full font-mono text-[11px] break-all transition`}
      data-testid="dns-copy-btn"
      title="Click to copy"
    >
      <span className="flex-1 min-w-0 break-all">{value}</span>
      {copied ? <Check size={12} className="text-emerald-400 shrink-0" /> : <Copy size={12} className="opacity-50 group-hover:opacity-100 shrink-0" />}
    </button>
  );
}

export default function EmailDomainWidget() {
  const [data, setData] = useState({ domains: [], from_env: "" });
  const [loading, setLoading] = useState(true);
  const [newDomain, setNewDomain] = useState("");
  const [adding, setAdding] = useState(false);
  const [verifying, setVerifying] = useState("");
  const [removing, setRemoving] = useState("");
  const [expanded, setExpanded] = useState({});
  const [detail, setDetail] = useState({}); // {domainId: fullDoc with records}

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/email/domains");
      setData(res.data || { domains: [], from_env: "" });
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load domains");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (d) => {
    const open = !expanded[d.id];
    setExpanded((e) => ({ ...e, [d.id]: open }));
    if (open && !detail[d.id]) {
      try {
        const res = await api.get(`/admin/email/domains/${d.id}`);
        setDetail((x) => ({ ...x, [d.id]: res.data }));
      } catch (e) {
        toast.error("Could not fetch DNS records");
      }
    }
  };

  const addDomain = async (e) => {
    e.preventDefault();
    const name = (newDomain || "").trim().toLowerCase();
    if (!name) return;
    setAdding(true);
    try {
      await api.post("/admin/email/domains", { name });
      toast.success(`Added ${name} — scroll down for DNS records`);
      setNewDomain("");
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Could not add domain");
    } finally {
      setAdding(false);
    }
  };

  const verify = async (d) => {
    setVerifying(d.id);
    try {
      const res = await api.post(`/admin/email/domains/${d.id}/verify`);
      if (res.data?.records) setDetail((x) => ({ ...x, [d.id]: res.data }));
      await load();
      toast.success("Verification triggered — status will update shortly");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Verify failed");
    } finally {
      setVerifying("");
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove ${d.name} from Resend? Emails from this domain will stop delivering.`)) return;
    setRemoving(d.id);
    try {
      await api.delete(`/admin/email/domains/${d.id}`);
      toast.success("Removed");
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not remove");
    } finally {
      setRemoving("");
    }
  };

  const usingResendDev = (data.from_env || "").includes("resend.dev");

  return (
    <div className="space-y-5" data-testid="email-domain-widget">
      <div>
        <div className="flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-[#FF8A00]">
          <Mail size={12} /> Email sending
        </div>
        <h3 className="font-display text-2xl mt-1">Sending domain</h3>
        <p className="text-sm text-white/55 mt-1">
          Verify your domain so welcome + intake emails land in inboxes (not spam). Resend blocks sending to random addresses until you verify a domain.
        </p>
        <div className="mt-3 text-[12px] text-white/60">
          <span className="text-white/40">Currently sending from:</span>{" "}
          <span className="font-mono">{data.from_env || "(not configured)"}</span>
          {usingResendDev && (
            <div className="mt-2 inline-flex items-start gap-2 px-3 py-2 rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-300 text-[11px]">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              You're still sending from <span className="font-mono mx-1">onboarding@resend.dev</span> — restrictive, only your verified email receives mail. Verify a domain below and we'll switch the From address automatically.
            </div>
          )}
        </div>
      </div>

      {/* Add domain */}
      <form onSubmit={addDomain} className="glass rounded-xl p-4 flex items-end gap-3" data-testid="add-domain-form">
        <div className="flex-1">
          <Label className="text-[11px] tracking-[0.18em] uppercase text-white/50">Add a sending domain</Label>
          <Input
            value={newDomain}
            onChange={(e) => setNewDomain(e.target.value)}
            placeholder="drthrive365.com"
            data-testid="new-domain-input"
            className="bg-white/5 border-white/10 h-10"
          />
        </div>
        <Button
          type="submit"
          disabled={adding || !newDomain.trim()}
          data-testid="add-domain-btn"
          className="h-10 rounded-full bg-gradient-to-r from-[#FF8A00] to-[#E52E71] text-white px-5"
        >
          {adding ? <><Loader2 size={14} className="animate-spin mr-2" /> Adding…</> : <><Plus size={14} className="mr-2" /> Add</>}
        </Button>
      </form>

      {/* Domain list */}
      {loading ? (
        <div className="flex items-center justify-center py-12 text-white/40"><Loader2 className="animate-spin" /></div>
      ) : (data.domains || []).length === 0 ? (
        <div className="text-sm text-white/50 italic">No domains yet. Add one above.</div>
      ) : (
        <div className="space-y-3">
          {data.domains.map((d) => {
            const full = detail[d.id];
            const records = (full?.records) || [];
            return (
              <div key={d.id} className="glass rounded-xl overflow-hidden border border-white/5" data-testid={`domain-row-${d.name}`}>
                <div className="px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex items-center gap-3">
                    <div className="font-mono text-sm">{d.name}</div>
                    <StatusPill status={d.status} />
                    <span className="text-[10px] text-white/35">{d.region}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => toggle(d)}
                      data-testid={`domain-toggle-${d.name}`}
                      className="h-8 bg-transparent border-white/15 text-xs"
                    >
                      {expanded[d.id] ? "Hide DNS" : "Show DNS records"}
                    </Button>
                    {d.status !== "verified" && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => verify(d)}
                        disabled={verifying === d.id}
                        data-testid={`domain-verify-${d.name}`}
                        className="h-8 bg-white/10 hover:bg-white/15 text-xs"
                      >
                        {verifying === d.id ? <Loader2 size={12} className="animate-spin" /> : <><RefreshCw size={12} className="mr-1.5" /> Verify</>}
                      </Button>
                    )}
                    <button
                      onClick={() => remove(d)}
                      disabled={removing === d.id}
                      className="p-2 text-white/35 hover:text-red-400 transition"
                      data-testid={`domain-delete-${d.name}`}
                      title="Remove domain"
                    >
                      {removing === d.id ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                    </button>
                  </div>
                </div>
                {expanded[d.id] && (
                  <div className="px-4 pb-4 pt-1 border-t border-white/5" data-testid={`domain-records-${d.name}`}>
                    <div className="text-[11px] text-white/45 mb-3">
                      Add these records in your DNS provider (GoDaddy, Cloudflare, Namecheap, etc). Once added, click <strong>Verify</strong> above. Propagation can take 5-30 minutes.
                    </div>
                    {!full ? (
                      <div className="text-xs text-white/40 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading records…</div>
                    ) : records.length === 0 ? (
                      <div className="text-xs text-white/40 italic">No DNS records returned by Resend.</div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead className="text-[10px] tracking-[0.18em] uppercase text-white/40">
                          <tr>
                            <th className="text-left pb-2 pr-3">Type</th>
                            <th className="text-left pb-2 pr-3">Name / Host</th>
                            <th className="text-left pb-2 pr-3">Value</th>
                            <th className="text-left pb-2 pr-3">TTL / Priority</th>
                            <th className="text-left pb-2">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {records.map((r, i) => (
                            <tr key={i} className="border-t border-white/5 align-top">
                              <td className="py-2 pr-3 font-mono">{r.type}</td>
                              <td className="py-2 pr-3"><CopyField value={r.name} dense /></td>
                              <td className="py-2 pr-3 max-w-[320px]"><CopyField value={r.value} dense /></td>
                              <td className="py-2 pr-3 text-white/55">
                                {r.ttl || "Auto"}{r.priority ? ` · prio ${r.priority}` : ""}
                              </td>
                              <td className="py-2"><StatusPill status={r.status || "not_started"} /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
